// @ts-strict-ignore
import { ArticleVO } from '@article/model/vo/article.vo';
import { IArticleConnectProcessor } from './i-article-connect.processor';
import { ArticleUtils } from '@article/util/article-utils';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { DefaultSingleConnectionConnectProcessor } from './default-single-connection-connect.processor';
import { DynamicLabelingArticleConnectProcessor } from './dynamic-labeling-article-connect.processor';
import { BusbarArticleConnectProcessor } from './busbar-article-connect.processor';
import { LabelingArticleConnectProcessor } from './labeling-article-connect.processor';
import { WireBridgeArticleConnectProcessor } from './wire-bridge-article-connect.processor';
import { DefaultMultipleConnectionConnectProcessor } from './default-multiple-connection-connect.processor';
import { BridgeArticleConnectProcessor } from './bridge-article-connect.processor';
import { UnusedLabelingArticleConnectProcessor } from './unused-labeling-article-connect.processor';
import { UnusedDynamicLabelingArticleConnectProcessor } from './unused-dynamic-labeling-article-connect.processor';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ArticleConnectProcessorRegistry {
  private _defaultSingleConnectProcessor: DefaultSingleConnectionConnectProcessor;
  private _dynamicLabelingArticleConnectProcessor: DynamicLabelingArticleConnectProcessor;
  private _busbarArticleConnectProcessor: BusbarArticleConnectProcessor;
  private _labelingConnectProcessor: LabelingArticleConnectProcessor;
  private _wireBridgeConnectProcessor: WireBridgeArticleConnectProcessor;
  private _unusedLabelingConnectProcessor: UnusedLabelingArticleConnectProcessor;
  private _unusedDynamicLabelingArticleConnectProcessor: UnusedDynamicLabelingArticleConnectProcessor;
  private _defaultMultipleConnectProcessor: DefaultMultipleConnectionConnectProcessor;
  private _bridgeConnectProcessor: BridgeArticleConnectProcessor;

  private initialized = false;

  public initialize(defaultSingleConnectProcessor: DefaultSingleConnectionConnectProcessor,
                    dynamicLabelingArticleConnectProcessor: DynamicLabelingArticleConnectProcessor,
                    busbarArticleConnectProcessor: BusbarArticleConnectProcessor,
                    labelingConnectProcessor: LabelingArticleConnectProcessor,
                    wireBridgeConnectProcessor: WireBridgeArticleConnectProcessor,
                    unusedLabelingConnectProcessor: UnusedLabelingArticleConnectProcessor,
                    unusedDynamicLabelingArticleConnectProcessor: UnusedDynamicLabelingArticleConnectProcessor,
                    // prepare processors to connect article by multiple giving connections:
                    defaultMultipleConnectProcessor: DefaultMultipleConnectionConnectProcessor,
                    bridgeConnectProcessor: BridgeArticleConnectProcessor) {
    this._defaultSingleConnectProcessor = defaultSingleConnectProcessor;
    this._dynamicLabelingArticleConnectProcessor = dynamicLabelingArticleConnectProcessor;
    this._busbarArticleConnectProcessor = busbarArticleConnectProcessor;
    this._labelingConnectProcessor = labelingConnectProcessor;
    this._wireBridgeConnectProcessor = wireBridgeConnectProcessor;
    this._unusedLabelingConnectProcessor = unusedLabelingConnectProcessor;
    this._unusedDynamicLabelingArticleConnectProcessor = unusedDynamicLabelingArticleConnectProcessor;
    this._defaultMultipleConnectProcessor = defaultMultipleConnectProcessor;
    this._bridgeConnectProcessor = bridgeConnectProcessor;
    this.initialized = true;
  }

  public get(article: ArticleVO, connectionSetId: string, isUnusedLabeling: boolean = false): IArticleConnectProcessor {

    if (!this.initialized) {
      throw new Error("ArticleConnectProcessorRegistry not initialized!")
    }

    if (!article) {
      return null;
    }
    const connectByMultipleAreas: boolean = this.isConnectByMultipleAreas(article, connectionSetId);
    return ArticleUtils.isCompositeArticle(article)
      ? this.getDefaultArticleProcessor(connectByMultipleAreas)
      : this.getSimpleArticleProcessor(article, connectByMultipleAreas, isUnusedLabeling);
  }

  private getDefaultArticleProcessor(connectByMultipleAreas: boolean): IArticleConnectProcessor {
    return connectByMultipleAreas ? this._defaultMultipleConnectProcessor : this._defaultSingleConnectProcessor;
  }

  private getSimpleArticleProcessor(article: ArticleVO, connectByMultipleAreas: boolean, isUnusedLabeling: boolean): IArticleConnectProcessor {
    if (ArticleUtils.isLabelingArticle(article)) {
      if (ArticleUtils.isDynamicArticle(article)) {
        return isUnusedLabeling ? this._unusedDynamicLabelingArticleConnectProcessor : this._dynamicLabelingArticleConnectProcessor;
      } else {
        return isUnusedLabeling ? this._unusedLabelingConnectProcessor : this._labelingConnectProcessor;
      }
    } else if (ArticleUtils.isWireBridgeArticle(article)) {
      return this._wireBridgeConnectProcessor;
    } else if (ArticleUtils.isBridge(article)) {
      return this._bridgeConnectProcessor;
    } else if (ArticleUtils.isBusbarOrCoverArticle(article)) {
      return this._busbarArticleConnectProcessor;
    } else {
      return this.getDefaultArticleProcessor(connectByMultipleAreas);
    }
  }

  private isConnectByMultipleAreas(article: ArticleVO, connectionSetId: string): boolean {
    const givingAreasInSet: ConnectionAreaVO[] = ArticleUtils.getGivingConnectionsSetFromArticle(
      !article.baseArticle ? article : article.baseArticle,
      connectionSetId
    );
    return givingAreasInSet.length > 1;
  }
}
